import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import QuestionViewer from './QuestionViewer';
import Lottie from 'lottie-react';
import aiAnswerAnimation from '../animations/generate_ai_answer.json';
import api from "../axiosClient";
import { useLocation } from "react-router-dom";
import { trackEvent } from '../utils/analytics';
import styles from './GeneratePaper.module.css';

function GeneratePaper() {
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [stepIndex, setStepIndex] = useState(0);
  const [selections, setSelections] = useState({});
  const selectionsRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [viewerMode, setViewerMode] = useState(false);
  const [generatedPaper, setGeneratedPaper] = useState(null);

  const [boardOptions, setBoardOptions] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [showChapterSelection, setShowChapterSelection] = useState(false);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const category = params.get("category") || "school";
  const [programOptions, setProgramOptions] = useState([]);
  const [streamOptions, setStreamOptions] = useState([]);

  const steps = category === "college"
    ? ['syllabus', 'program', 'stream', 'grade', 'subject', 'term', 'difficulty', 'done']
    : ['syllabus', 'grade', 'subject', 'term', 'difficulty', 'done'];

  const stepLabels = {
    syllabus: 'Board',
    program: 'Program',
    stream: 'Stream',
    grade: category === "college" ? 'Semester' : 'Class',
    subject: 'Subject',
    term: 'Exam Type',
    difficulty: 'Difficulty',
    done: 'Done'
  };

  const step = steps[stepIndex];

  useEffect(() => {
    trackEvent("generate_page_view", { category });
  }, [category]);

  useEffect(() => {
    if (step && step !== 'done') {
      trackEvent("generate_step_view", { category, step });
    }
  }, [category, step]);

  useEffect(() => {
    if (category === "college") {
      api.get("/api/questions/programs")
        .then(res => setProgramOptions(res.data))
        .catch(console.error);
    }

    api.get(`/api/questions/boards?category=${encodeURIComponent(category)}`)
      .then(res => {
        const data = res.data;
        if (Array.isArray(data)) setBoardOptions(data);
        else if (Array.isArray(data?.boards)) setBoardOptions(data.boards);
        else setBoardOptions([]);
      })
      .catch(console.error);
  }, [category]);

const handleSelect = (value) => {
    const key = step;
    trackEvent("generate_step_selected", { category, step: key, value });
    const updated = { ...selectionsRef.current, [key]: value };
    selectionsRef.current = updated;
    setSelections(updated);

    if (key === "syllabus") {
      setSelections(prev => ({ ...prev, [key]: value }));
      const newSelections = { ...selectionsRef.current, [key]: value };
      selectionsRef.current = newSelections;

      if (category === "college") {
        api.get("/api/questions/programs")
          .then(res => setProgramOptions(res.data || []))
          .catch(console.error);
      }

      api.get(`/api/questions/grades?board=${encodeURIComponent(value)}`)
        .then(res => {
          const data = res.data;
          if (Array.isArray(data)) setGradeOptions(data);
          else if (Array.isArray(data?.grades)) setGradeOptions(data.grades);
          else setGradeOptions([]);
          setStepIndex(prev => prev + 1);
        })
        .catch(console.error);
      return;
    }

    if (key === "program") {
      const newSel = { ...selectionsRef.current, program: value };
      selectionsRef.current = newSel;
      setSelections(newSel);
      delete newSel.stream;
      delete newSel.grade;
      delete newSel.subject;
      delete newSel.term;
      delete newSel.difficulty;
      delete newSel.chapters;
      setStreamOptions([]);
      setSubjectOptions([]);
      api.get(`/api/questions/streams?program=${encodeURIComponent(value)}`)
        .then(res => {
          setStreamOptions(res.data || []);
          setStepIndex(prev => prev + 1);
        })
        .catch(console.error);
      return;
    }

    if (key === "stream") {
      const newSel = { ...selectionsRef.current, stream: value };
      selectionsRef.current = newSel;
      setSelections(newSel);
      setSubjectOptions([]);
      const url = `/api/questions/grades?board=${newSel.syllabus}&program=${newSel.program}&stream=${newSel.stream}`;
      api.get(url)
        .then(res => {
          const data = res.data;
          if (Array.isArray(data)) setGradeOptions(data);
          else if (Array.isArray(data?.grades)) setGradeOptions(data.grades);
          else setGradeOptions([]);
          setStepIndex(prev => prev + 1);
        })
        .catch(console.error);
      return;
    }

    if (key === "grade") {
      const newSel = { ...selectionsRef.current, grade: value };
      selectionsRef.current = newSel;
      setSelections(newSel);
      let url = `/api/questions/subjects?board=${newSel.syllabus}&grade=${value}`;
      if (category === "college") {
        url += `&program=${newSel.program}&stream=${newSel.stream}`;
      }
      api.get(url)
        .then(res => {
          setSubjectOptions(res.data);
          setStepIndex(prev => prev + 1);
        })
        .catch(console.error);
      return;
    }

    setStepIndex(prev => prev + 1);
  };

  const handleSubjectSelect = (value) => {
    const newSel = { ...selectionsRef.current, subject: value };
    selectionsRef.current = newSel;
    setSelections(newSel);
    trackEvent("generate_step_selected", { category, step: "subject", value });
    setStepIndex(prev => prev + 1);
  };

  const renderOptions = () => {
    if (step === "syllabus") {
      if (!boardOptions.length) return <p className={styles.loading}>Loading boards...</p>;
      return boardOptions.map(b => (
        <Option key={b.id} label={b.name} onClick={() => handleSelect(b.name)} />
      ));
    }
    if (step === "program") {
      if (!programOptions.length) return <p className={styles.loading}>Loading programs...</p>;
      return programOptions.map(p => (
        <Option key={p.id} label={p.name} onClick={() => handleSelect(p.name)} />
      ));
    }
    if (step === "stream") {
      if (!streamOptions.length) return <p className={styles.loading}>Loading streams...</p>;
      return streamOptions.map(s => (
        <Option key={s.id} label={s.name} onClick={() => handleSelect(s.name)} />
      ));
    }
    if (step === "grade") {
      if (!gradeOptions.length) return <p className={styles.loading}>Loading...</p>;
      return gradeOptions.map(g => (
        <Option
          key={g.id}
          label={category === "college" ? (g.display_name || `Semester ${g.grade_number}`) : `Class ${g.grade_number}`}
          onClick={() => handleSelect(g.grade_number)}
        />
      ));
    }
    if (step === "subject") {
      if (!subjectOptions.length) return <p className={styles.loading}>Loading...</p>;
      return subjectOptions.map(s => (
        <Option key={s.id} label={s.name} onClick={() => handleSubjectSelect(s.name)} />
      ));
    }
    if (step === "term") {
    }
    if (step === "stream") {
      if (!streamOptions.length) return <p className={styles.loading}>No streams found</p>;
      return streamOptions.map(s => (
        <Option key={s.id} label={s.name} onClick={() => handleSelect(s.name)} />
      ));
    }
    if (step === "term") {
      return (
        <>
          {['Mid Term', 'Annual Exam'].map(t => (
            <Option key={t} label={t} onClick={() => handleSelect(t)} />
          ))}
          <Option
            label="Select Chapters"
            onClick={() => {
              trackEvent("chapter_selection_opened", {
                category,
                board: selections.syllabus,
                grade: selections.grade,
                subject: selections.subject
              });
              setShowChapterSelection(true);
              api.get(
                `/api/questions/chapters?board=${selections.syllabus}&grade=${selections.grade}&subject=${selections.subject}`
              ).then(res => setChapterOptions(res.data)).catch(console.error);
            }}
          />
        </>
      );
    }
    if (step === "difficulty") {
      return ['Easy', 'Medium', 'Hard'].map(d => (
        <Option key={d} label={d} onClick={() => handleSelect(d)} />
      ));
    }
  };

  const generatePaperFromBackend = async (selections) => {
    const res = await api.post("/api/generate-question-paper", { selections });
    const paper = res.data.questionPaper;
    console.log(`[GeneratePaper] Received: type=${typeof paper}, sections=${paper?.sections?.length || 0}`);
    return paper;
  };

const ChapterSelector = () => {
    const [selectedChapters, setSelectedChapters] = useState(selections.chapters || []);

    return (
      <div className={styles.chapterOverlay}>
        <motion.div
          className={styles.chapterModal}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
        >
          <div className={styles.chapterHeader}>
            <h2>Select Chapters</h2>
            <button className={styles.chapterClose} onClick={() => setShowChapterSelection(false)}>×</button>
          </div>
          <div className={styles.chapterGrid}>
            {chapterOptions.map(ch => {
              const chapterName = ch.chapter_name || ch.name;
              return (
                <button
                  key={ch.id}
                  className={`${styles.chapterChip} ${selectedChapters.includes(chapterName) ? styles.selected : ''}`}
                  onClick={() => {
                    const newList = selectedChapters.includes(chapterName)
                      ? selectedChapters.filter(c => c !== chapterName)
                      : [...selectedChapters, chapterName];
                    setSelectedChapters(newList);
                  }}
                >
                  {chapterName}
                </button>
              );
            })}
          </div>
          <button
            className={styles.doneButton}
            onClick={() => {
              trackEvent("chapter_selection_done", {
                category,
                chapter_count: selectedChapters.length
              });
              setSelections({ ...selections, chapters: selectedChapters });
              setShowChapterSelection(false);
              setStepIndex(prev => prev + 1);
            }}
          >
            Done ({selectedChapters.length} selected)
          </button>
        </motion.div>
      </div>
    );
  };

  if (showChapterSelection) return <ChapterSelector />;

  if (viewerMode) {
    return <QuestionViewer questionPaperText={generatedPaper} meta={selections} />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>ZenQB Question Paper Generator</h1>
        <p className={styles.subtitle}>
          {category === "college" ? "Create custom exam papers for college" : "Create custom exam papers for school"}
        </p>
      </div>

      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          style={{ width: `${((stepIndex) / (steps.length - 1)) * 100}%` }}
        />
      </div>

      <div className={styles.stepIndicator}>
        <span className={styles.stepBadge}>
          Step {stepIndex + 1} of {steps.length - 1}
        </span>
        <span className={styles.stepLabel}>{stepLabels[step] || step}</span>
      </div>

      <AnimatePresence mode="wait">
        {step !== 'done' ? (
          <motion.div
            key={step}
            className={styles.optionsPanel}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <h3 className={styles.stepTitle}>Select {stepLabels[step]}</h3>
            <div className={styles.optionsGrid}>
              {renderOptions()}
            </div>
          </motion.div>
        ) : (
          <motion.div
            className={styles.donePanel}
            key="done"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className={styles.summaryCard}>
              <h3>Ready to Generate</h3>
              <div className={styles.summaryList}>
                {Object.entries(selections).map(([key, val]) => (
                  <div key={key} className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>{stepLabels[key] || key}</span>
                    <span className={styles.summaryValue}>{String(val)}</span>
                  </div>
                ))}
              </div>
            </div>
            <button
              className={styles.generateButton}
              disabled={loading}
              onClick={async () => {
                trackEvent("paper_generation_started", {
                  category,
                  board: selections.syllabus,
                  grade: selections.grade,
                  subject: selections.subject,
                  term: selections.term,
                  difficulty: selections.difficulty
                });
                try {
                  setLoading(true);
                  const paper = await generatePaperFromBackend(selections);
                  setGeneratedPaper(paper);
                  setViewerMode(true);
                  trackEvent("paper_generation_completed", {
                    category,
                    board: selections.syllabus,
                    grade: selections.grade,
                    subject: selections.subject
                  });
                } catch (err) {
                  console.error(err);
                  trackEvent("paper_generation_failed", {
                    category,
                    board: selections.syllabus,
                    grade: selections.grade,
                    subject: selections.subject
                  });
                  alert("Failed to generate paper.");
                } finally {
                  setLoading(false);
                }
              }}
            >
              {loading ? 'Generating...' : 'Generate Paper'}
            </button>
            {loading && <Lottie animationData={aiAnswerAnimation} loop style={{ width: 150, margin: '0 auto' }} />}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Option({ label, onClick, selected }) {
  return (
    <motion.button
      className={`${styles.optionCard} ${selected ? styles.selected : ''}`}
      whileHover={{ scale: 1.04, y: -2 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
    >
      <span className={styles.optionLabel}>{label}</span>
      {selected && <span className={styles.checkmark}>✓</span>}
    </motion.button>
  );
}

export default GeneratePaper;